import Button from '@/src/components/atom/Button';
import Dialog from '@/src/components/atom/Dialog';
import Tabs from '@/src/components/molecule/Tabs';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useRef, useState } from 'react';
import { CreditTemplateDto } from '@/src/models/CreditTemplateDto';
import { PlusIcon } from 'lucide-react';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import {
  getCreditTemplateAPI,
  getCreditTemplateNameAndCountAPI,
  postCreditTemplateBulkUpsertAPI,
} from '@/src/apis/rest/creditTemplate';
import { GetCreditTemplateRequestParams } from '@/src/apis/rest/creditTemplate.dtos';
import { toast } from '@/src/hooks/useToast';
import { TOAST_CONST } from '@/src/constants/toast';
import { OPTIONS_CONST } from '@/src/constants/options';
import Typo from '@/src/components/atom/Typo';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import { UNIT_STRINGS } from '@/src/constants/unit';

interface CreditTemplateManagementModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function CreditTemplateManagementModal({
  open: openProps,
  onOpenChange: onOpenChangeProps,
}: CreditTemplateManagementModalProps) {
  // util
  const {
    Dialog: SaveAlertDialog,
    isOpen: isSaveAlertOpen,
    openDialog: openSaveAlertDialog,
    closeDialog: closeSaveAlertDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const {
    Dialog: BlockAlertDialog,
    isOpen: isBlockAlertOpen,
    openDialog: openBlockAlertDialog,
    closeDialog: closeBlockAlertDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  // state
  const [category, setCategory] = useState<
    GetCreditTemplateRequestParams['category'] | null
  >(null);
  const [isAdditional, setIsAdditional] = useState(false);
  const [tableData, setTableData] = useState<CreditTemplateDto[]>([]);
  const customTableRef = useRef<AgGridReact>(null);
  const [isDirty, setIsDirty] = useState(false);

  // query
  const {
    data: creditTemplateNameAndCountData,
    isFetched: isCreditTemplateNameAndCountFeched,
    refetch: refetchCreditTemplateNameAndCountData,
  } = useQuery({
    queryKey: ['credit-template-list'],
    queryFn: getCreditTemplateNameAndCountAPI,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const { data: creditTemplateData, refetch: refetchCreditTemplateData } =
    useQuery({
      queryKey: ['credit-template', category],
      queryFn: () => {
        return getCreditTemplateAPI({
          page: 1,
          pageSize: 9999,
          category: category || '',
        });
      },
      enabled: isCreditTemplateNameAndCountFeched && !!category,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

  // mutate
  const postCreditTemplateBulkUpsertMutation = useMutation({
    mutationFn: postCreditTemplateBulkUpsertAPI,
  });

  // event
  const handleCreditTemplateUpsert = () => {
    postCreditTemplateBulkUpsertMutation.mutate(
      {
        creditTemplates: tableData.map(item => ({
          id: item.id || null,
          category: category as string,
          title: item.title,
          credit: item.credit,
          isActive: item.isActive,
          isEditable: item.isEditable,
        })),
      },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });
          refetchCreditTemplateNameAndCountData();
          refetchCreditTemplateData();
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };
  const handleCloseModal = () => {
    if (isDirty) {
      openBlockAlertDialog();
    } else {
      onOpenChangeProps(false);
    }
  };
  const handleBlockModalOk = () => {
    onOpenChangeProps(false);
  };
  const handleOkClick = () => {
    openSaveAlertDialog();
  };

  // render
  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          className="flex-1"
          buttonColor="outline"
          onClick={handleCloseModal}
        >
          {BUTTON_CONST.cancel}
        </Button>
        <Button
          className="flex-1"
          onClick={handleOkClick}
          disabled={
            !isDirty ||
            !tableData
              ?.map(item => !!item.title && !!item.credit)
              .every(item => !!item)
          }
          isLoading={postCreditTemplateBulkUpsertMutation.isPending}
        >
          {BUTTON_CONST.save}
        </Button>
      </div>
    );
  };
  const renderAdministratorCreditTabs = () => {
    const target = creditTemplateNameAndCountData?.rows?.find(
      item => item.category === '관리자 지정',
    );

    return target ? (
      <Tabs.SingleTrigger
        value={target.category}
        label={`${target.category} (${target.count})`}
      />
    ) : null;
  };

  const handleTableDataChange = (key: string, value: any, index: number) => {
    setIsDirty(true);
    setTableData(prev => {
      const writeCommunityCategory = prev.find(
        item => item.title.replaceAll(' ', '') === '글작성',
      );

      if (
        category?.replaceAll(' ', '') === '커뮤니티' &&
        writeCommunityCategory
      ) {
        return [
          writeCommunityCategory,
          ...prev
            .filter(item => item.title !== '글 작성')
            .map((item, itemIndex) =>
              itemIndex === index ? { ...item, [key]: value } : item,
            ),
        ];
      }

      return prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item,
      );
    });
  };

  const handleRemoveTableData = (index: number) => {
    setTableData(prev => prev.filter((_, idx) => index !== idx));
  };

  const handleSaveClick = () => {
    handleCreditTemplateUpsert();

    setIsDirty(false);
  };

  // effect
  useEffect(() => {
    setCategory(prev => {
      return (
        prev ||
        creditTemplateNameAndCountData?.rows?.filter(
          item => item.category !== '관리자 지정',
        )?.[0]?.category ||
        null
      );
    });
  }, [creditTemplateNameAndCountData]);

  useEffect(() => {
    setTableData(creditTemplateData?.rows || []);
  }, [creditTemplateData]);

  useEffect(() => {
    const target = creditTemplateNameAndCountData?.rows?.find(
      item => item.category === category,
    );

    setIsAdditional(!!target?.isEditable);
  }, [category, creditTemplateNameAndCountData]);

  return (
    <Dialog
      open={openProps}
      onOpenChange={handleCloseModal}
      contentProps={{
        style: {
          width: 'calc(100vw - 24px)',
        },
        className: 'max-w-[995px]',
      }}
      title="크레딧 카테고리 관리"
      footer={renderFooter()}
    >
      <div className="flex flex-col gap-4">
        {category && (
          <Tabs
            defaultValue={category}
            onChange={e => {
              setIsDirty(false);
              setCategory(e);
            }}
          >
            <Tabs.TabsList>
              {(
                creditTemplateNameAndCountData?.rows?.filter(
                  item => item.category !== '관리자 지정',
                ) || []
              ).map(item => {
                return (
                  <Tabs.SingleTrigger
                    value={item.category}
                    label={`${item.category} (${item.count})`}
                    key={item.category}
                  />
                );
              })}
              {renderAdministratorCreditTabs()}
            </Tabs.TabsList>
          </Tabs>
        )}
        <div className="flex h-[50vh] min-h-[200px] flex-col gap-2">
          <article
            className="flex h-[full] flex-col rounded-lg border border-gray0"
            style={{ overflow: 'overlay' }}
          >
            <header className="bg-gray0">
              <div className="flex text-b14m text-gray7 [&>th]:border-r [&>th]:border-gray1">
                <div className="flex w-[8%] items-center justify-center py-3">
                  No
                </div>
                <div className="flex w-[26%] items-center justify-center py-3">
                  하위 카테고리
                </div>
                <div className="flex w-[26%] items-center justify-center py-3">
                  {`크레딧(${UNIT_STRINGS.CREDIT})`}
                </div>
                <div className="flex w-[20%] items-center justify-center py-3">
                  사용여부
                </div>
                <div className="flex w-[20%] items-center justify-center border-none py-3">
                  삭제
                </div>
              </div>
            </header>
            <div className="flex flex-1 flex-col overflow-auto overflow-y-auto">
              {tableData
                .filter(item =>
                  category?.replaceAll(' ', '') === '커뮤니티'
                    ? item.title.replaceAll(' ', '') !== '글작성'
                    : item,
                )
                .map((item, idx) => (
                  <div key={idx.toString()} className="flex">
                    <div className="flex w-[8%] items-center justify-center">
                      <p className="flex h-11 items-center justify-center px-2 text-b14r text-gray9">
                        {(idx + 1).toString()}
                      </p>
                    </div>
                    <div className="flex w-[26%] items-center justify-center">
                      <div className="flex h-11 w-full items-center justify-center px-2">
                        {item.isEditable ? (
                          <Input
                            autoFocus
                            containerClassName="w-full"
                            size={32}
                            value={item.title}
                            onChange={e =>
                              handleTableDataChange(
                                'title',
                                e.target.value,
                                idx,
                              )
                            }
                            onClear={() => {
                              handleTableDataChange('title', '', idx);
                            }}
                            maxLength={30}
                          />
                        ) : (
                          <p className="flex h-11 items-center justify-center px-2 text-b14r text-gray9">
                            {item.title}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex w-[26%] items-center justify-center">
                      <div className="flex h-11 w-full items-center justify-center px-2">
                        <Input
                          type="number"
                          min={0}
                          containerClassName="w-full"
                          size={32}
                          value={item.credit}
                          onChange={e =>
                            handleTableDataChange('credit', e.target.value, idx)
                          }
                          onClear={() => {
                            handleTableDataChange('credit', 0, idx);
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex w-[20%] items-center justify-center">
                      <div className="flex h-11 w-full items-center justify-center px-2">
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.activated, value: 'true' },
                            {
                              label: OPTIONS_CONST.notActivated,
                              value: 'false',
                            },
                          ]}
                          rootProps={{
                            value: item.isActive ? 'true' : 'false',
                            onValueChange: e => {
                              handleTableDataChange(
                                'isActive',
                                e === 'true',
                                idx,
                              );
                            },
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex w-[20%] items-center justify-center">
                      <div className="flex h-11 w-full items-center justify-center px-2">
                        <Button
                          buttonColor="red"
                          buttonSize="small"
                          disabled={!!item.id}
                          onClick={() => handleRemoveTableData(idx)}
                        >
                          {BUTTON_CONST.delete}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
            {isAdditional && (
              <footer>
                <div>
                  <div className="w-full p-2">
                    <Button
                      buttonColor="outline"
                      className="flex w-full items-center gap-2"
                      onClick={() => {
                        setIsDirty(true);

                        setTableData(prev => {
                          const result = [...prev, new CreditTemplateDto()];

                          customTableRef.current?.api.setRowData(result);

                          return result;
                        });
                      }}
                    >
                      <PlusIcon size={16} /> 크레딧 추가하기
                    </Button>
                  </div>
                </div>
              </footer>
            )}
          </article>
          {category?.replaceAll(' ', '') === '커뮤니티' && (
            <p className="mt-auto flex gap-1 text-gray5">
              <span className="flex h-[18px] translate-y-[0.5px] items-center">
                <LucideIcon name="Info" size={16} color="gray5" />
              </span>
              <Typo typoType="b14r" className="!text-gray5">
                커뮤니티 크레딧 일일 지급한도는 게시글 최대 1,000C / 댓글 최대
                100C이며, 초과 시 마지막 크레딧까지 지급된 후 지급되지 않습니다.
                (매일 00시 갱신)
              </Typo>
            </p>
          )}
        </div>
      </div>

      {/* 알럿 */}
      <SaveAlertDialog
        open={isSaveAlertOpen}
        onOpenChange={closeSaveAlertDialog}
        onCancel={() => {
          closeSaveAlertDialog();
        }}
        onOk={handleSaveClick}
        title={`${category} 크레딧 카테고리 수정`}
        okButtonProps={{
          isLoading: postCreditTemplateBulkUpsertMutation.isPending,
        }}
      >
        수정된 크레딧는 신규 지급에만 적용됩니다
      </SaveAlertDialog>
      <BlockAlertDialog
        open={isBlockAlertOpen}
        onOpenChange={closeBlockAlertDialog}
        onCancel={() => {
          closeBlockAlertDialog();
        }}
        onOk={handleBlockModalOk}
        title="취소"
      >
        변경사항은 저장되지 않습니다. 취소하시겠습니까?
      </BlockAlertDialog>
    </Dialog>
  );
}
